import { UploadIcon } from '@heroicons/react/outline';
import createRepoDocument from 'api/v2/repository/createRepoDocument';
import deleteDocument from 'api/v2/repository/deleteDocument';
import { uploadFile } from 'api/v2/upload';
import classNames from 'classnames';
import { Pagination } from 'components/atoms';
import { Button } from 'components/atoms/Button';
import { Modal } from 'components/atoms/Modal';
import { Title } from 'components/atoms/Title';
import { SizesEnum } from 'components/atoms/Title/Title.enum';
import CustomLoader from 'components/molecules/CustomLoader.component';
import Header from 'components/molecules/Header/Header.component';
import ListLoadingSkeleton from 'components/molecules/ListLoadingSkeleton';
import SimpleError from 'components/molecules/SimpleError.component';
import { TableDeleteBulkAction } from 'components/molecules/TableDeleteBulkAction.component';
import { FileUploader } from 'components/organisms/FileUploader';
import { FiltersV3 } from 'components/organisms/Filters/Filters.component';
import useDocumentsRepositoryFilters from 'hooks/DocumentsRepository/useDocumentsRepositoryFilters';
import useGetRepositoryTags from 'hooks/DocumentsRepository/useGetRepositoryTags';
import { worldThemeContext } from 'hooks/shared/useWorldTheme';
import type { FormSlideOverManagerApi } from 'hooks/useFormSlideOverManager';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { DocumentsRepositoryCacheKeys } from 'types/cacheKeys.types';
import type { DocumentItem } from 'types/repository.types';
import { World } from 'types/shared/worlds';
import { UploadTypesEnum } from 'types/uploadEnums.types';
import { getTracingEvents } from 'utils/amplitude';
import {
  WEBAPP_DOCUMENTSREPOSITORY_DOCUMENTADDEDMANUALLY,
  WEBAPP_DOCUMENTSREPOSITORY_OPENEDDOCUMENT,
} from 'utils/amplitude/events/repository.amplitude';
import showToast from 'utils/toast';
import DocumentsTable from './DocumentsTable.component';
import ExportDocuments from './ExportDocuments.component';
import {
  DropzoneContent,
  FilesListFooter,
  FilesListHeader,
} from './fileUploader.content';
import FormSlideOver from './FormSlideOver';
import { repoSkeletonColumns } from './skeletons';
import useDocuments from './useDocuments';

const tracingEvents = getTracingEvents({
  onOpenDocument: WEBAPP_DOCUMENTSREPOSITORY_OPENEDDOCUMENT,
  onUploadSuccess: WEBAPP_DOCUMENTSREPOSITORY_DOCUMENTADDEDMANUALLY,
});

const DocumentsRepository: FC = () => {
  const { t } = useTranslation();

  const [isDragging, setIsDragging] = useState(false);

  const { filtersConfig, filtersQuery } = useDocumentsRepositoryFilters();

  const documentsQuery = useDocuments();

  const tagsQuery = useGetRepositoryTags();

  const formSlideOverRef = useRef<FormSlideOverManagerApi<DocumentItem>>();

  const queryClient = useQueryClient();

  const [currentOpenExpenseId, setCurrentOpenExpenseId] = useState('');

  const [highlightedDocumentsIds, setHighlightedDocumentsIds] = useState<
    string[]
  >([]);

  const openDocumentDetails = (
    document: DocumentItem,
    options?: Partial<any>,
  ) => formSlideOverRef.current?.open(document, options);

  const pagination = useMemo(() => {
    if (documentsQuery.query.data?.paging?.pageCount > 1) {
      return (
        <div className="items-end pt-4 w-full flex justify-end">
          <Pagination
            className="mt-2"
            pagesCount={documentsQuery.query.data?.paging?.pageCount || 0}
            perPage={15}
            totalCount={documentsQuery.query.data?.paging?.totalCount || 0}
            page={documentsQuery.meta.page}
            currentPageCount={
              documentsQuery.query.data?.repositoryDocuments?.length || 0
            }
            onPageChange={(pageNumber) => {
              documentsQuery.controllers.setPageNumber(pageNumber);
            }}
          />
        </div>
      );
    }
  }, [documentsQuery]);

  return (
    <worldThemeContext.Provider value="primary">
      <div>
        <FormSlideOver
          apiRef={formSlideOverRef}
          onOpen={(doc) => {
            setCurrentOpenExpenseId(doc._id);
          }}
          onClose={() => {
            setCurrentOpenExpenseId('');
          }}
        />

        <Header
          headerTitle={
            <Title type={SizesEnum.h1} className="text-5xl">
              {t('repository.title')}
            </Title>
          }
          color={World.REPOSITORY}
          className="!py-5"
          headerControllers={
            <Modal
              name="add document to repository"
              width="5xl"
              className="lg:h-3/4 lg:max-h-[500px] lg:min-h-[400px]"
              trigger={(api) => (
                <Button icon={UploadIcon} onClick={api.openModal}>
                  {t('repository.upload_file_cta')}
                </Button>
              )}
              render={(api) => (
                <FileUploader
                  onUpload={(api) =>
                    uploadFile({
                      file: api.file,
                      category: UploadTypesEnum.documentsRepository,
                    }).then((res: any) =>
                      createRepoDocument({
                        filename: res.key.split('/').pop(),
                        name: api.file.name,
                      }).then((doc) => {
                        tracingEvents.onUploadSuccess();

                        return doc;
                      }),
                    )
                  }
                  afterPatchUpload={(fileUploaderMeta) => {
                    if (fileUploaderMeta.done) {
                      tagsQuery.refetch();

                      documentsQuery.controllers.reset();

                      const newDocs = fileUploaderMeta.done.map(
                        (doc) => doc.result,
                      );

                      setHighlightedDocumentsIds((prev) => [
                        ...prev,
                        ...newDocs.map((doc) => doc._id),
                      ]);
                    }
                  }}
                  dropzoneProps={{
                    className: 'flex-none lg:flex-1',
                    render: (dropzoneMeta, fileUploaderMeta) => (
                      <DropzoneContent
                        dropzoneMeta={dropzoneMeta}
                        fileUploaderMeta={fileUploaderMeta}
                      />
                    ),
                  }}
                  filesListProps={{
                    renderHeader: (fileUploaderMeta) => (
                      <FilesListHeader fileUploaderMeta={fileUploaderMeta} />
                    ),
                    renderFooter: (fileUploaderMeta) => {
                      const firstDocument = fileUploaderMeta?.done?.[0]?.result;

                      return (
                        <FilesListFooter
                          fileUploaderMeta={fileUploaderMeta}
                          onViewDocument={() => {
                            api.closeModal();

                            openDocumentDetails(firstDocument as DocumentItem);
                          }}
                        />
                      );
                    },
                  }}
                />
              )}
            />
          }
        />

        <div className="flex items-center justify-between my-5">
          <FiltersV3
            containerClassName="max-w-2xl"
            searchPlaceholder={`${t('repository.search_placeholder')}`}
            filtersConfig={filtersConfig}
            filters={filtersQuery.data || []}
            onClickToggle={() => {
              if (filtersQuery.isError) filtersQuery.refetch();
            }}
            isLoading={filtersQuery.isLoading || tagsQuery.isLoading}
            isError={filtersQuery.isError || tagsQuery.isError}
          />

          <ExportDocuments filters={filtersConfig.filtersData} />

          {documentsQuery.meta.selectedRows.length > 0 ? (
            <div className="ml-auto flex-shrink-0 flex gap-4 self-center">
              <div className="flex items-center gap-4">
                <p className="text-primary-500">{`${documentsQuery.meta.selectedRows.length} selected`}</p>

                <div className="h-7 w-[2px] bg-primary-100" />

                <Button
                  structure="text"
                  color="primary"
                  className="underline"
                  onClick={() => documentsQuery.controllers.setSelectedRows([])}
                >
                  {t('repository.deselect_cta')}
                </Button>

                <TableDeleteBulkAction
                  structure="secondary"
                  className="ml-auto self-center"
                  selected={documentsQuery.meta.selectedRows}
                  onDelete={(ids) => Promise.all(ids.map(deleteDocument))}
                  onAfterDelete={() => {
                    queryClient.invalidateQueries(
                      DocumentsRepositoryCacheKeys.HasDocuments,
                    );

                    documentsQuery.controllers.reset();
                  }}
                  title={t('repository.delete_documents.title')}
                  description={t('repository.delete_documents.description', {
                    numOfDocs: documentsQuery.meta.selectedRows.length,
                  })}
                />
              </div>
            </div>
          ) : null}
        </div>

        {documentsQuery.query.isError || tagsQuery.isError ? (
          <SimpleError onRetry={() => documentsQuery.query.refetch()} />
        ) : documentsQuery.query.isLoading || tagsQuery.isLoading ? (
          <div className="w-full">
            <ListLoadingSkeleton columns={repoSkeletonColumns} />
          </div>
        ) : (
          <FileUploader
            className="relative flex-col-reverse"
            renderUploadList={(_, uploaderMeta) => {
              const isUploading = (uploaderMeta?.uploading?.length || 0) > 0;

              return (
                <AnimatePresence
                  onExitComplete={() =>
                    showToast(
                      { title: t('repository.uploading_docs.success_msg') },
                      { type: 'success' },
                    )
                  }
                >
                  {!isUploading ? null : (
                    <motion.div
                      key="overlay"
                      initial={{ opacity: 0 }}
                      exit={{ opacity: 0 }}
                      animate={{ opacity: 1, transition: { duration: 0.4 } }}
                      className="absolute top-0 right-0 bottom-0 left-0 flex flex-col items-center justify-center bg-primary bg-opacity-40 rounded-lg"
                    >
                      <CustomLoader color="white" className="mb-4" />

                      <Title
                        type={SizesEnum.h1}
                        className="text-4xl !text-white"
                      >
                        {t('repository.uploading_docs.title')}
                      </Title>

                      <p className="text-white mt-2">
                        {t('repository.uploading_docs.subtitle')}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              );
            }}
            onUpload={(api) =>
              uploadFile({
                file: api.file,
                category: UploadTypesEnum.documentsRepository,
              }).then(({ key }: any) =>
                createRepoDocument({
                  filename: key.split('/').pop(),
                  name: api.file.name,
                }).then((doc) => {
                  tracingEvents.onUploadSuccess();

                  return doc;
                }),
              )
            }
            afterPatchUpload={(fileUploaderMeta) => {
              if (fileUploaderMeta.done) {
                tagsQuery.refetch();

                documentsQuery.controllers.reset();

                const newDocs = fileUploaderMeta.done.map((doc) => doc.result);

                setHighlightedDocumentsIds((prev) => [
                  ...prev,
                  ...newDocs.map((doc) => doc._id),
                ]);
              }
            }}
            color="basic"
            dropzoneProps={{
              onClick: () => null,
              className: '!border-0',
              renderWrapperClassName: '!p-0 !h-auto',
              render: (_, fileUploaderMeta) => {
                const isUploading =
                  (fileUploaderMeta?.uploading?.length || 0) > 0;

                return (
                  <div
                    className="relative w-full"
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                    }}
                  >
                    {isDragging && (
                      <div className="absolute inset-0 bg-primary-50/50 border-2 border-dashed border-primary-300 rounded-lg z-10 flex items-center justify-center"></div>
                    )}

                    <div
                      className={classNames('w-full min-h-[400px]', {
                        'blur-sm': isUploading,
                      })}
                    >
                      <DocumentsTable
                        {...documentsQuery.meta}
                        setTableSort={(cellId) => {
                          documentsQuery.controllers.setTableSort(cellId);
                        }}
                        setSelectedRows={(ids) => {
                          documentsQuery.controllers.setSelectedRows(ids);
                        }}
                        data={documentsQuery.query.data?.repositoryDocuments}
                        isLoading={
                          documentsQuery.query.isLoading ||
                          documentsQuery.query.isFetching
                        }
                        isError={documentsQuery.query.isError}
                        onRetry={() => documentsQuery.query.refetch()}
                        isEmpty={
                          documentsQuery.meta.isCurrentFiltersDataEmpty &&
                          documentsQuery.query.data?.repositoryDocuments
                            ?.length === 0
                        }
                        highlight={[currentOpenExpenseId]}
                        flash={highlightedDocumentsIds}
                        afterFlash={(_id) =>
                          setHighlightedDocumentsIds((prev) =>
                            prev.filter((id) => id !== _id),
                          )
                        }
                        onRowClick={(_, document) => {
                          tracingEvents.onOpenDocument();

                          return openDocumentDetails?.(
                            document as DocumentItem,
                          );
                        }}
                      />
                      {pagination}
                    </div>
                  </div>
                );
              },
            }}
            onAfterDropAccepted={(processFiles) => {
              window.scrollTo({ top: 230, behavior: 'smooth' });
              return processFiles();
            }}
            filesListProps={{
              renderHeader: () => null,
              renderFooter: () => null,
            }}
          />
        )}
      </div>
    </worldThemeContext.Provider>
  );
};

export default DocumentsRepository;
